import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Upload, Plus, Trash2, Eye, Palette, Image, Type, Layout } from 'lucide-react';
import API from '../lib/api';

const TEMPLATES = [
  { id: 'moderna', name: 'Moderna', desc: 'Diseno llamativo con formas decorativas y hero grande' },
  { id: 'minimalista', name: 'Minimalista', desc: 'Ultra limpio, centrado, sin distracciones' },
  { id: 'corporativa', name: 'Corporativa', desc: 'Profesional con header, hero y barra de confianza' },
];

export default function PortalSettingsPage() {
  const [settings, setSettings] = useState({
    slug: '',
    welcome_message: '',
    primary_color: '#FF6B35',
    background_image_url: '',
    logo_url: '',
    sections: [],
    template: 'moderna'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await API.get('/portal/settings');
      setSettings(res.data);
      if (res.data.slug) {
        setPreviewUrl(`${window.location.origin}/empresa/${res.data.slug}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/portal/settings', settings);
      if (settings.slug) {
        setPreviewUrl(`${window.location.origin}/empresa/${settings.slug}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/portal/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, [field]: res.data.url }));
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const addSection = () => {
    setSettings(prev => ({
      ...prev,
      sections: [...prev.sections, { title: '', content: '', icon: 'star' }]
    }));
  };

  const updateSection = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeSection = (index) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Portal Publico</h1>
          <p className="text-gray-500 mt-1 text-sm">Personaliza la pagina de bienvenida para tus trabajadores</p>
        </div>
        <div className="flex gap-2">
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-orange-200 hover:bg-orange-50" data-testid="preview-portal-btn">
                <Eye className="w-4 h-4 mr-2" />
                Ver Portal
              </Button>
            </a>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200"
            data-testid="save-portal-btn"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column - Settings */}
        <div className="space-y-5">
          {/* Slug */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">URL del Portal</h3>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Slug (identificador unico)</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 whitespace-nowrap">/empresa/</span>
                <Input
                  value={settings.slug}
                  onChange={(e) => setSettings(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="mi-empresa"
                  className="flex-1"
                  data-testid="portal-slug-input"
                />
              </div>
              {settings.slug && (
                <p className="text-xs text-gray-400 mt-1">
                  Tu portal estara en: {window.location.origin}/empresa/{settings.slug}
                </p>
              )}
            </div>
          </div>

          {/* Welcome message */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Type className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">Mensaje de Bienvenida</h3>
            </div>
            <textarea
              className="w-full h-24 rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={settings.welcome_message}
              onChange={(e) => setSettings(prev => ({ ...prev, welcome_message: e.target.value }))}
              placeholder="Bienvenido al portal de empleados de nuestra empresa..."
              data-testid="portal-welcome-input"
            />
          </div>

          {/* Color */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">Color Principal</h3>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={settings.primary_color}
                onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-12 h-12 rounded-lg cursor-pointer border border-gray-200"
                data-testid="portal-color-input"
              />
              <Input
                value={settings.primary_color}
                onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                placeholder="#FF6B35"
                className="w-32"
              />
              <div className="w-20 h-10 rounded-lg" style={{ backgroundColor: settings.primary_color }} />
            </div>
          </div>

          {/* Images */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">Imagenes</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2 block">Logo</Label>
                <div className="flex items-center gap-3">
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt="Logo" className="w-12 h-12 object-contain rounded-lg border border-gray-200" />
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={(e) => handleUpload(e, 'logo_url')} className="hidden" />
                    <Button variant="outline" size="sm" className="border-orange-200 hover:bg-orange-50" asChild disabled={uploading}>
                      <span><Upload className="w-4 h-4 mr-1" />{uploading ? 'Subiendo...' : 'Subir Logo'}</span>
                    </Button>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2 block">Imagen de Fondo</Label>
                <div className="flex items-center gap-3">
                  {settings.background_image_url && (
                    <img src={settings.background_image_url} alt="Fondo" className="w-20 h-12 object-cover rounded-lg border border-gray-200" />
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={(e) => handleUpload(e, 'background_image_url')} className="hidden" />
                    <Button variant="outline" size="sm" className="border-orange-200 hover:bg-orange-50" asChild disabled={uploading}>
                      <span><Upload className="w-4 h-4 mr-1" />{uploading ? 'Subiendo...' : 'Subir Fondo'}</span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Template & Sections */}
        <div className="space-y-5">
          {/* Template selector */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layout className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900">Plantilla de Diseno</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSettings(prev => ({ ...prev, template: t.id }))}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    settings.template === t.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 hover:border-orange-200 hover:bg-orange-50/30'
                  }`}
                  data-testid={`template-${t.id}-btn`}
                >
                  <div className={`w-full h-16 rounded-lg mb-3 ${
                    t.id === 'moderna' ? 'bg-gradient-to-br from-orange-100 to-orange-200' :
                    t.id === 'minimalista' ? 'bg-gray-50 border border-gray-100' :
                    'bg-gradient-to-b from-gray-100 to-white border border-gray-200'
                  } flex items-center justify-center`}>
                    {t.id === 'moderna' && <div className="w-8 h-8 rounded-lg bg-orange-400 opacity-60" />}
                    {t.id === 'minimalista' && <div className="w-6 h-6 rounded-full bg-gray-300" />}
                    {t.id === 'corporativa' && (
                      <div className="space-y-1">
                        <div className="w-12 h-1 bg-gray-300 rounded" />
                        <div className="w-8 h-1 bg-gray-200 rounded" />
                      </div>
                    )}
                  </div>
                  <p className="font-semibold text-sm text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Secciones de Contenido</h3>
              <Button variant="outline" size="sm" onClick={addSection} className="border-orange-200 hover:bg-orange-50" data-testid="add-section-btn">
                <Plus className="w-4 h-4 mr-1" /> Seccion
              </Button>
            </div>

            {settings.sections.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                Anade secciones para mostrar informacion adicional en tu portal
              </p>
            ) : (
              <div className="space-y-4">
                {settings.sections.map((section, idx) => (
                  <div key={idx} className="bg-[#FFF8F4] border border-orange-100 rounded-lg p-4 relative">
                    <button
                      onClick={() => removeSection(idx)}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="space-y-3">
                      <Input
                        value={section.title}
                        onChange={(e) => updateSection(idx, 'title', e.target.value)}
                        placeholder="Titulo de la seccion"
                        className="bg-white"
                      />
                      <textarea
                        className="w-full h-20 rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none bg-white"
                        value={section.content}
                        onChange={(e) => updateSection(idx, 'content', e.target.value)}
                        placeholder="Contenido de la seccion..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Preview */}
          <div className="bg-white border border-orange-100 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Vista Previa</h3>
            <div
              className="rounded-xl overflow-hidden border border-gray-200 h-64 relative flex items-center justify-center"
              style={{
                backgroundColor: settings.primary_color + '15',
                backgroundImage: settings.background_image_url ? `url(${settings.background_image_url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {settings.background_image_url && (
                <div className="absolute inset-0 bg-black/30" />
              )}
              <div className="relative z-10 text-center p-4">
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo" className="w-14 h-14 object-contain mx-auto mb-3 rounded-xl" />
                )}
                <h4 className="font-bold text-lg" style={{ color: settings.background_image_url ? 'white' : settings.primary_color }}>
                  {settings.company_name || 'Tu Empresa'}
                </h4>
                <p className="text-sm mt-1 max-w-xs" style={{ color: settings.background_image_url ? 'rgba(255,255,255,0.8)' : '#6B7280' }}>
                  {settings.welcome_message || 'Mensaje de bienvenida'}
                </p>
                <div
                  className="mt-3 px-4 py-2 rounded-lg text-white text-xs font-semibold inline-block"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  Acceder al Portal
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
